"use client";

import { auth } from "@onsand/auth";
import { sendPasswordResetEmail } from "firebase/auth";
import { CircleAlert, Loader2, Mail } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import {
	AUTH_FIELD,
	AUTH_FIELD_ICON,
	AUTH_LABEL,
	AUTH_SUBMIT,
} from "@/components/auth/field-styles";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { type ResetErrorKey, resetErrorKey } from "@/lib/camp/auth/auth-errors";
import { setMailLanguage } from "@/lib/camp/auth/mail-language";

type Reset =
	| { status: "idle" }
	| { status: "sending" }
	| { status: "sent" }
	| { key: ResetErrorKey; status: "refused" };

/** Asks for the address and sends Firebase's reset link. The answer never says who has an account. */
export function ForgotPassword({
	initialEmail,
	onBack,
}: {
	initialEmail: string;
	onBack: () => void;
}) {
	const t = useTranslations("login");
	const locale = useLocale();
	const [email, setEmail] = useState(initialEmail);
	const [reset, setReset] = useState<Reset>({ status: "idle" });

	async function submit(event: FormEvent) {
		event.preventDefault();
		setReset({ status: "sending" });
		try {
			setMailLanguage(auth, locale);
			await sendPasswordResetEmail(auth, email.trim());
			setReset({ status: "sent" });
		} catch (error) {
			const key = resetErrorKey(error);
			setReset(key === null ? { status: "sent" } : { key, status: "refused" });
		}
	}

	return (
		<div className="w-full">
			<h1 className="text-balance font-heading font-semibold text-3xl leading-tight tracking-tight">
				{t("resetTitle")}
			</h1>
			<p className="mt-2 text-base text-muted-foreground">{t("resetDescription")}</p>

			<form className="mt-8 grid gap-5" onSubmit={submit}>
				<div className="grid gap-2">
					<Label className={AUTH_LABEL} htmlFor="reset-email">
						{t("email")}
					</Label>
					<InputGroup className={AUTH_FIELD}>
						<InputGroupAddon>
							<Mail aria-hidden="true" className={AUTH_FIELD_ICON} />
						</InputGroupAddon>
						<InputGroupInput
							autoCapitalize="none"
							autoComplete="email"
							id="reset-email"
							inputMode="email"
							name="email"
							onChange={(event) => setEmail(event.target.value)}
							placeholder={t("emailPlaceholder")}
							required
							spellCheck={false}
							type="email"
							value={email}
						/>
					</InputGroup>
				</div>

				{reset.status === "sent" && (
					<p className="text-pretty text-muted-foreground text-sm" role="status">
						{t("resetDone")}
					</p>
				)}
				{reset.status === "refused" && (
					<p
						className="flex items-start gap-2 text-pretty text-destructive-foreground text-sm"
						role="alert"
					>
						<CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
						{t(reset.key)}
					</p>
				)}

				<Button className={AUTH_SUBMIT} disabled={reset.status === "sending"} type="submit">
					{reset.status === "sending" ? (
						<>
							<Loader2 aria-hidden="true" className="size-5 animate-spin" />
							{t("resetSending")}
						</>
					) : (
						t("resetSend")
					)}
				</Button>
				<Button onClick={onBack} type="button" variant="ghost">
					{t("backToSignIn")}
				</Button>
			</form>
		</div>
	);
}
